import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  TablePagination,
  Tabs,
  Tab,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  MyLocation as GpsIcon,
  QrCode as QrIcon,
  Create as ManualIcon,
  PersonOff as AbsentIcon,
  CheckCircleOutline as CheckedIcon,
  CheckCircle as PresentCircleIcon,
  Cancel as AbsentCircleIcon,
  HourglassEmpty as IncompleteCircleIcon,
  Update as LateCircleIcon,
  Warning as HalfDayCircleIcon,
  Timer as HoursIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  getSchoolReport,
  getAbsentToday,
  markAbsent,
  overrideRecord,
  TeacherAttendanceResponse,
  StaffDto,
  TeacherAttendanceOverrideRequest,
} from "../../services/teacherAttendanceService";
import { AttendanceOverrideModal } from "./AttendanceOverrideModal";

export const AdminDailyRoster: React.FC = () => {
  const { t } = useTranslation();
  
  // Date selection (default today)
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toLocaleDateString("en-CA") // YYYY-MM-DD in local time
  );
  
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(0); // 0: Checked-In Roster, 1: Absent Today
  
  // Roster states
  const [roster, setRoster] = useState<TeacherAttendanceResponse[]>([]);
  const [absents, setAbsents] = useState<StaffDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination states for checked-in roster
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);

  // Override modal state
  const [overrideOpen, setOverrideOpen] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<TeacherAttendanceResponse | null>(null);

  const isToday = selectedDate === new Date().toLocaleDateString("en-CA");

  // Fetch roster & absents
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch roster report for selected date
      const reportPage = await getSchoolReport(selectedDate, selectedDate, page, rowsPerPage);
      setRoster(reportPage.content);
      setTotalElements(reportPage.totalElements);

      // Fetch absents only if selected date is today
      if (isToday) {
        const absentList = await getAbsentToday();
        setAbsents(absentList);
      } else {
        setAbsents([]);
        if (activeTab === 1) setActiveTab(0); // fallback to roster tab
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || t("failed_to_fetch_attendance_data") || "Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate, page, rowsPerPage]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Mark a staff member absent or leave
  const handleMarkAbsent = async (staffId: number, status: string = "ABSENT") => {
    try {
      setActionLoading(true);
      setError(null);
      await markAbsent(staffId, selectedDate, status);
      await fetchData(); // refresh logs
    } catch (err: any) {
      setError(err?.response?.data?.message || t("failed_to_mark_absent") || "Failed to mark teacher absent");
    } finally {
      setActionLoading(false);
    }
  };

  // Open override modal
  const handleOpenOverride = (record: TeacherAttendanceResponse) => {
    setSelectedRecord(record);
    setOverrideOpen(true);
  };

  // Save manual override
  const handleSaveOverride = async (id: number, payload: TeacherAttendanceOverrideRequest) => {
    await overrideRecord(id, payload);
    await fetchData(); // reload roster
  };

  // Format local time
  const formatLocalTime = (isoString: any): string => {
    if (!isoString) return "-";
    try {
      let date: Date;
      if (isoString instanceof Date) {
        date = isoString;
      } else if (typeof isoString === 'number') {
        date = new Date(isoString < 10000000000 ? isoString * 1000 : isoString);
      } else if (Array.isArray(isoString)) {
        const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = isoString;
        const ms = Math.floor(nano / 1000000);
        date = new Date(year, month - 1, day, hour, minute, second, ms);
      } else {
        const dateStr = String(isoString).trim();
        if (/^\d+(\.\d+)?$/.test(dateStr)) {
          const num = Number(dateStr);
          date = new Date(num < 10000000000 ? num * 1000 : num);
        } else {
          let normalized = dateStr;
          if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalized)) {
            normalized = normalized.replace(/\s+/, 'T');
          }
          const m = normalized.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.(\d{1,3})\d*)?/);
          if (m) {
            const finalNorm = `${m[1]}T${m[2]}${m[3] ? '.' + m[3].padEnd(3, '0') : ''}`;
            date = new Date(finalNorm);
          } else {
            date = new Date(normalized);
          }
        }
      }

      if (isNaN(date.getTime())) return String(isoString);
      return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch (e) {
      return "-";
    }
  };


  const formatWorkingHours = (totalHours: number | null): string => {
    if (totalHours === null || totalHours === undefined || isNaN(totalHours)) return "-";
    const totalMinutes = Math.round(totalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Get status color chip
  const getStatusChip = (status: string) => {
    const s = status ? status.toUpperCase() : "";
    switch (s) {
      case "PRESENT":
        return <Chip label={t("PRESENT", "Present")} color="success" size="small" variant="filled" icon={<PresentCircleIcon />} />;
      case "LATE":
        return <Chip label={t("LATE", "Late")} color="warning" size="small" variant="filled" icon={<LateCircleIcon />} />;
      case "HALF_DAY":
        return <Chip label={t("HALF_DAY", "Half Day")} sx={{ bgcolor: "orange", color: "white" }} size="small" variant="filled" icon={<HalfDayCircleIcon />} />;
      case "ABSENT":
        return <Chip label={t("ABSENT", "Absent")} color="error" size="small" variant="filled" icon={<AbsentCircleIcon />} />;
      case "LEAVE":
        return <Chip label={t("LEAVE", "Leave")} color="secondary" size="small" variant="filled" icon={<AbsentCircleIcon />} />;
      case "INCOMPLETE":
      default:
        return <Chip label={t("INCOMPLETE", "Incomplete")} color="default" size="small" variant="filled" icon={<IncompleteCircleIcon />} />;
    }
  };

  // Check-in method icon
  const getMethodIcon = (method: string) => {
    const m = method ? method.toUpperCase() : "";
    switch (m) {
      case "GPS":
        return (
          <Tooltip title={t("gps_checkin", "GPS Coordinates Verification")}>
            <GpsIcon color="primary" fontSize="small" />
          </Tooltip>
        );
      case "QR":
        return (
          <Tooltip title={t("qr_checkin", "Rotating QR Code Scanned")}>
            <QrIcon color="secondary" fontSize="small" />
          </Tooltip>
        );
      case "MANUAL":
        return (
          <Tooltip title={t("manual_override", "Manual Administrator Correction")}>
            <ManualIcon color="action" fontSize="small" />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  // Filter roster by search term
  const filteredRoster = roster.filter((r) => {
    const hasSearch = searchTerm.trim().toLowerCase();
    if (!hasSearch) return true;
    const matchesName = r.staffName?.toLowerCase().includes(hasSearch);
    const empId = r.employeeId?.trim();
    const hasEmpId = empId && empId !== "-";
    const matchesEmpId = hasEmpId ? empId.toLowerCase().includes(hasSearch) : false;
    return matchesName || matchesEmpId;
  });

  const filteredAbsents = absents.filter((a) => {
    const hasSearch = searchTerm.trim().toLowerCase();
    if (!hasSearch) return true;
    const matchesName = `${a.firstName} ${a.lastName}`.toLowerCase().includes(hasSearch);
    const empId = a.employeeId?.trim();
    const hasEmpId = empId && empId !== "-";
    const matchesEmpId = hasEmpId ? empId.toLowerCase().includes(hasSearch) : false;
    return matchesName || matchesEmpId;
  });

  // Compute metrics for today's summary cards
  const totalCheckedIn = roster.filter(r => r.status !== "ABSENT" && r.status !== "LEAVE").length;
  const totalLate = roster.filter(r => r.status === "LATE").length;
  const totalIncomplete = roster.filter(r => r.status === "INCOMPLETE" || !r.checkOutTime).length;
  const totalAbsent = roster.filter(r => r.status === "ABSENT").length + absents.length;

  return (
    <Box sx={{ py: 2 }}>
      {/* Summary and Filters row (styled like Application Form management) */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Left: Summary Cards */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {/* Checked In */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "#e8f5e8",
              border: "1px solid #4caf50",
              minWidth: 120,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="success.main" fontWeight="bold">
              {totalCheckedIn}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("checked_in", "Checked In")}
            </Typography>
          </Box>

          {/* Late Check-Ins */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "#fff8e1",
              border: "1px solid #ff9800",
              minWidth: 120,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="warning.main" fontWeight="bold">
              {totalLate}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("late_checkins", "Late Check-Ins")}
            </Typography>
          </Box>

          {/* Pending Check-Out */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "#e3f2fd",
              border: "1px solid #90caf9",
              minWidth: 120,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              {totalIncomplete}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("pending_checkout", "Pending Check-Out")}
            </Typography>
          </Box>

          {/* Total Absent */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              minWidth: 120,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="error.main" fontWeight="bold">
              {totalAbsent}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("absent", "Total Absent")}
            </Typography>
          </Box>
        </Box>

        {/* Right: Search + Date + Refresh */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder={t("search_teacher_placeholder", "Search teacher by name or Employee ID...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          <TextField
            type="date"
            label={t("attendance_date", "Attendance Date")}
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(0);
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
              textTransform: "none",
              fontWeight: "medium",
            }}
            onClick={() => fetchData()}
            disabled={loading}
            size="medium"
          >
            {t("refresh", "Refresh Data")}
          </Button>
        </Box>
      </Box>

      {/* Roster & Absent Tabs (only show tabs if checking today) */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab label={t("checked_in_roster", "Attendance Roster")} id="roster-tab" />
          {isToday && (
            <Tab
              label={`${t("absent_today", "Not Logged Yet")} (${absents.length})`}
              id="absent-tab"
              disabled={loading}
            />
          )}
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : activeTab === 0 ? (
        <TableContainer key="attendance-roster-container" component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>{t("teacher_name", "Teacher Name")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("check_in", "Check In")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("check_out", "Check Out")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("method", "Method")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("hours", "Hours")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("status", "Status")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="right">{t("actions", "Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoster.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {t("no_attendance_logs", "No attendance logs found for this date.")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRoster.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.staffName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{formatLocalTime(row.checkInTime)}</TableCell>
                    <TableCell align="center">{formatLocalTime(row.checkOutTime)}</TableCell>
                    <TableCell align="center">{getMethodIcon(row.checkInMethod)}</TableCell>
                    <TableCell align="center">
                      {row.totalHours !== null && row.totalHours !== undefined ? (
                        <Box display="inline-flex" alignItems="center">
                          <HoursIcon fontSize="inherit" sx={{ mr: 0.5, color: "text.secondary" }} />
                          <Typography variant="body2">{formatWorkingHours(row.totalHours)}</Typography>
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">{getStatusChip(row.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("override_record", "Override & Edit times")}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenOverride(row)}
                          disabled={actionLoading}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      ) : (
        <TableContainer key="not-logged-yet-container" component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>{t("teacher_name", "Teacher Name")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>{t("employee_id", "Employee ID")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>{t("department", "Department")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="right">{t("actions", "Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAbsents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {t("all_teachers_logged", "All active teachers have logged attendance for today.")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAbsents.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.firstName} {row.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.employeeId || "-"}</TableCell>
                    <TableCell>{row.department || "-"}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<AbsentIcon />}
                          onClick={() => handleMarkAbsent(row.id, "ABSENT")}
                          disabled={actionLoading}
                        >
                          {t("mark_absent", "Mark Absent")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          startIcon={<AbsentIcon />}
                          onClick={() => handleMarkAbsent(row.id, "LEAVE")}
                          disabled={actionLoading}
                        >
                          {t("mark_leave", "Mark Leave")}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Override Dialog */}
      <AttendanceOverrideModal
        open={overrideOpen}
        onClose={() => {
          setOverrideOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        onSave={handleSaveOverride}
      />
    </Box>
  );
};
